// lib/licensing/license-gate.tsx
//
// Gate de React que envuelve la app on-prem. Si la licencia no es válida,
// muestra una pantalla de activación. Si lo es, dispara el heartbeat (si
// hay internet) y renderiza los children.
//
// En cloud/SaaS/Desktop el componente es transparente: renderiza children
// directamente sin evaluar nada (la suscripción del lado servidor basta).

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Lock,
  Database,
  Server,
  FileCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  evaluateOnPremLicense,
  guardReasonLabel,
  type GuardReason,
} from "./guard";
import {
  clearLicenseToken,
  loadLicenseToken,
  saveLastHeartbeatAt,
  saveLicenseToken,
} from "./storage";
import type { LicensePayload } from "@/convex/system/licensing/crypto";
import { LicenseProvider } from "./license-context";
import { AuthRightPanel, AuthMobileLogo } from "@/components/auth/auth-layout";
import { LeftPanel } from "@/components/layout/left-panel";
import { isFreeDesktopEdition, isLocalInstallEdition } from "../edition/edition";

// Copy del gate por edición. free_desktop comparte el mecanismo (token firmado)
// pero se presenta como la app local gratuita, no como el producto on-prem.
const GATE_BRANDING = {
  onprem: {
    badge: "Activación requerida",
    headline: "Activa Heskala On-prem",
    subtitle:
      "Ingresa el token de licencia que recibiste al comprar Heskala On-prem. La firma criptográfica se valida localmente, sin enviar el token a internet.",
    rightTitle: "Licencia de servidor",
    rightSubtitle: "Cada instalación requiere un token firmado por Heskala.",
    cardDescription: "Pegá el token que recibiste al comprar Heskala On-prem.",
    alertTitle: "Heskala On-prem",
    features: [
      { icon: Lock, label: "Datos aislados en tu servidor" },
      { icon: Database, label: "Base de datos propia" },
      { icon: Server, label: "Sin dependencia de la nube" },
      { icon: FileCheck, label: "Firma criptográfica del editor" },
    ],
  },
  free_desktop: {
    badge: "Activación gratuita",
    headline: "Activa Heskala Free",
    subtitle:
      "Ingresa el token de activación gratuito de Heskala Free. La firma se valida localmente en tu equipo, sin enviar nada a internet.",
    rightTitle: "Activación de Heskala Free",
    rightSubtitle: "Tu instalación local se activa con un token gratuito.",
    cardDescription: "Pegá el token de activación gratuito que recibiste.",
    alertTitle: "Heskala Free",
    features: [
      { icon: Lock, label: "100% local en tu equipo" },
      { icon: Database, label: "Tus datos nunca salen de tu computadora" },
      { icon: Server, label: "Sin Docker ni dependencias extra" },
      { icon: FileCheck, label: "Firma criptográfica del editor" },
    ],
  },
};

type Status =
  | { kind: "loading" }
  | { kind: "ok"; payload: LicensePayload }
  | { kind: "blocked"; reason: GuardReason };

function collapseWhitespace(s: string): string {
  return s.replace(/\s+/g, "").trim();
}

function extractLicenseId(token: string): string {
  const dot = token.indexOf(".");
  if (dot <= 0) return "";
  try {
    const payload = JSON.parse(
      atob(token.slice(0, dot).replace(/-/g, "+").replace(/_/g, "/")),
    ) as { lid?: string };
    return payload.lid ?? "";
  } catch {
    return "";
  }
}

/** Fingerprint estable de ESTA instalación (lo sirve el backend on-prem local). */
async function getInstallFingerprint(): Promise<string | undefined> {
  try {
    const res = await fetch("/license/fingerprint");
    if (!res.ok) return undefined;
    const body = (await res.json()) as { fingerprint?: string };
    return body.fingerprint || undefined;
  } catch {
    return undefined;
  }
}

async function tryHeartbeat(
  token: string,
): Promise<{ tier?: string } | void> {
  const lid = extractLicenseId(token);
  const fingerprint = await getInstallFingerprint();
  // El heartbeat va al CENTRAL (Heskala Cloud) si hay URL configurada; ahí vive
  // el record de la licencia y se hace el binding "una licencia = una
  // instalación". Sin URL (dev) cae al backend local, sin enforcement de binding.
  const base = (
    import.meta.env.VITE_LICENSE_SERVER_URL as string | undefined
  )?.replace(/\/+$/, "");
  try {
    const res = await fetch(`${base ?? ""}/license/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lid, token, fingerprint }),
    });
    if (!res.ok) throw new Error("heartbeat_endpoint_unreachable");
    const body = (await res.json()) as {
      valid?: boolean;
      serverTime?: number;
      reason?: string;
      /** tier token firmado con los módulos/seats vigentes del central */
      tier?: string;
    };

    if (body.valid === true) {
      saveLastHeartbeatAt(
        typeof body.serverTime === "number"
          ? body.serverTime
          : Math.floor(Date.now() / 1000),
      );
      // Devolvemos el tier firmado para que el gate sincronice la suscripción.
      return { tier: body.tier };
    }

    // La licencia ya fue activada en OTRA instalación (fingerprint distinto) →
    // bloqueamos: limpiamos el token y avisamos. Una licencia = una instalación.
    if (body.reason === "activated_on_other_server") {
      clearLicenseToken();
      toast.error(
        "Esta licencia ya fue activada en otra instalación. Las licencias on-prem son de un solo uso.",
      );
      return;
    }

    // El server no conoce la licencia todavía (DB vacía o recién creada).
    // La firma local ya pasó, así que la licencia es válida criptográficamente.
    // Aceptamos y dejamos que el server la registre en el próximo heartbeat.
    if (body.reason === "no_encontrada") {
      saveLastHeartbeatAt(Math.floor(Date.now() / 1000));
      return;
    }

    // Firma corrupta, lid no coincide, revocada, expirada, suspendida: token
    // genuinamente inválido → lo limpiamos.
    clearLicenseToken();
  } catch {
    // Sin endpoint de heartbeat (dev) o error de red: la firma local ya pasó,
    // confiamos en la verificación criptográfica y marcamos el heartbeat con
    // el reloj local. En prod on-prem el endpoint existe y esto no se ejecuta.
    saveLastHeartbeatAt(Math.floor(Date.now() / 1000));
  }
}

export function LicenseGate({
  children,
  publicSite = false,
}: {
  children: ReactNode;
  /**
   * Sitio público (`/s/<slug>`): sin licencia válida el visitante ve «no
   * disponible», no la pantalla de activación del ERP.
   */
  publicSite?: boolean;
}) {
  // Gate de activación para instalaciones locales (onprem y free_desktop). En
  // cloud (saas/desktop) es transparente.
  if (!isLocalInstallEdition()) {
    return <>{children}</>;
  }
  return <OnPremLicenseGate publicSite={publicSite}>{children}</OnPremLicenseGate>;
}

/** Lo que ve el visitante de un sitio público si la instalación no tiene licencia. */
export function PublicSiteUnavailable() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
      <p className="max-w-sm text-sm text-muted-foreground">Este sitio no está disponible por ahora. Vuelve a intentarlo más tarde.</p>
    </div>
  );
}

function OnPremLicenseGate({ children, publicSite }: { children: ReactNode; publicSite: boolean }) {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [tokenInput, setTokenInput] = useState("");

  // Token activado a nivel INSTALACIÓN (guardado en el backend, no por
  // navegador). Permite que cualquier navegador/dispositivo entre sin
  // re-activar. undefined = la query todavía está cargando.
  const serverLicense = useQuery(api.system.licensing.queries.getInstallLicense);
  const setInstallLicense = useMutation(
    api.system.licensing.mutations.setInstallLicense,
  );
  // Sincroniza módulos/seats desde el tier firmado que devuelve el heartbeat.
  const applyLicenseTier = useMutation(
    api.system.licensing.mutations.applyLicenseTier,
  );

  const recheck = useCallback(async () => {
    setStatus({ kind: "loading" });
    let token = loadLicenseToken();
    // Fallback: si ESTE navegador no tiene token pero la instalación ya está
    // activada en el backend, lo usamos (y lo cacheamos localmente).
    if (!token && serverLicense?.token) {
      token = serverLicense.token;
      saveLicenseToken(token);
    }
    // Backfill: instalación ya activada localmente (p. ej. tras un upgrade) pero
    // el backend todavía no tiene el token → lo propagamos para que otros
    // navegadores no tengan que re-activar. Verifica firma en el server.
    if (token && serverLicense !== undefined && !serverLicense?.token) {
      try {
        await setInstallLicense({ token });
      } catch {
        /* noop: si el token no es válido, el server no lo persiste */
      }
    }
    // free_desktop NO hace heartbeat ni binding contra el central: se valida
    // solo localmente (firma). Así corre 100% offline y sin la restricción
    // "una licencia = una instalación". El resto de ediciones sí lo hace.
    if (token && !isFreeDesktopEdition()) {
      const hb = await tryHeartbeat(token);
      // Aplica el tier (módulos/seats) firmado que devolvió el central, para
      // reflejar cambios hechos en el panel sin re-emitir el token.
      if (hb?.tier) {
        try {
          await applyLicenseTier({ tier: hb.tier });
        } catch {
          /* noop: si el tier no se puede aplicar, la app sigue como está */
        }
      }
    }
    const result = await evaluateOnPremLicense();
    if (result.allowed) {
      setStatus({ kind: "ok", payload: result.payload });
    } else {
      setStatus({ kind: "blocked", reason: result.reason });
    }
  }, [serverLicense, setInstallLicense, applyLicenseTier]);

  useEffect(() => {
    // Esperar a que la query del backend resuelva (undefined = cargando) para no
    // mostrar la pantalla de activación antes de saber si hay token server-side.
    if (serverLicense === undefined) return;
    void recheck();
  }, [serverLicense, recheck]);

  const onActivate = async () => {
    const cleaned = collapseWhitespace(tokenInput);
    if (!cleaned) {
      toast.error("Pegá un token de licencia");
      return;
    }
    saveLicenseToken(cleaned);
    setTokenInput("");
    // Persistir a nivel instalación para que otros navegadores no re-activen.
    // Si falla (firma inválida, etc.), la evaluación local de abajo decide.
    try {
      await setInstallLicense({ token: cleaned });
    } catch {
      /* noop: el chequeo local determina si el token sirve */
    }
    void recheck();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    setTokenInput(collapseWhitespace(pasted));
  };

  const onClear = () => {
    clearLicenseToken();
    setTokenInput("");
    void recheck();
  };

  if (status.kind === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent/50">
        <div className="flex items-center  text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" />

        </div>
      </div>
    );
  }

  if (status.kind === "ok") {
    return (
      <LicenseProvider payload={status.payload}>{children}</LicenseProvider>
    );
  }

  if (publicSite) return <PublicSiteUnavailable />;

  // Mostrar el input siempre que el usuario deba (re)ingresar un token: token
  // ausente/ inválido, o uno que no sirve para esta edición/está vencido. Sin
  // incluir wrong_edition/expired, un token equivocado dejaba trabada la app sin
  // forma de pegar el correcto.
  const showInput =
    status.reason === "no_token" ||
    status.reason === "verify_failed" ||
    status.reason === "no_heartbeat_yet" ||
    status.reason === "grace_exceeded" ||
    status.reason === "wrong_edition" ||
    status.reason === "expired";

  const brand = isFreeDesktopEdition()
    ? GATE_BRANDING.free_desktop
    : GATE_BRANDING.onprem;

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <LeftPanel
        badge={brand.badge}
        headline={brand.headline}
        subtitle={brand.subtitle}
        features={brand.features}
      />

      <AuthRightPanel>
        <div className="mb-8 text-center lg:text-left">
          <AuthMobileLogo />
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            {brand.rightTitle}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {brand.rightSubtitle}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              <CardTitle>Activa tu licencia</CardTitle>
            </div>
            <CardDescription>{brand.cardDescription}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-2">
            {status.reason !== "no_token" && (
              <Alert className="border-border">
                <ShieldCheck className="h-4 w-4" />
                <AlertTitle>{brand.alertTitle}</AlertTitle>
                <AlertDescription>
                  {guardReasonLabel(status.reason)}
                </AlertDescription>
              </Alert>
            )}

            {showInput && (
              <div className="space-y-2">
                <Label
                  htmlFor="license-token"
                  className="flex items-center gap-1.5"
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  Token de licencia
                </Label>
                <Input
                  id="license-token"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  onPaste={onPaste}
                  placeholder="eyJ…"
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground">
                  Pegá el token completo. Los saltos de línea y espacios se
                  ignoran automáticamente.
                </p>
              </div>
            )}
          </CardContent>

          {showInput && (
            <CardFooter className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClear}>
                Limpiar
              </Button>
              <Button onClick={onActivate}>Activar licencia</Button>
            </CardFooter>
          )}
        </Card>
      </AuthRightPanel>
    </div>
  );
}
